import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Chip, Stack, CircularProgress, Alert, Button, TextField, InputAdornment,
  Collapse, Badge,
} from '@mui/material';
import { Refresh as RefreshIcon, Place as DistanceIcon, FilterList as FilterIcon } from '@mui/icons-material';
import axios from 'axios';
import AvailableJobCard from './AvailableJobCard';
import logger from '../../utils/logger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const STORAGE_KEY = 'primeshine_maid_available_radius';
const JOB_TYPE_STORAGE_KEY = 'primeshine_maid_available_jobtype';
const SERVICE_TYPE_STORAGE_KEY = 'primeshine_maid_available_servicetype';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const RADIUS_OPTIONS = [5, 10, 25, 50, 100];

const JOB_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'RESIDENTIAL', label: 'Residential' },
  { value: 'VACATION_RENTAL', label: 'Vacation Rental' },
  { value: 'COMMERCIAL', label: 'Commercial' },
];

const SERVICE_TYPE_OPTIONS = [
  { value: '', label: 'All Services' },
  { value: 'regular', label: 'Regular' },
  { value: 'deep_clean', label: 'Deep Clean' },
  { value: 'move_in', label: 'Move In/Out' },
  { value: 'one_time', label: 'One Time' },
  { value: 'post_construction', label: 'Post Construction' },
];

// Groups the (already date-sorted) job list under "Today" / "Tomorrow" / weekday
// headers, matching how a maid actually thinks about her queue.
const formatDateGroup = (iso) => {
  const d = new Date(iso);
  const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = new Date();
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const tomorrowOnly = new Date(todayOnly);
  tomorrowOnly.setDate(todayOnly.getDate() + 1);

  if (dOnly.getTime() === todayOnly.getTime()) return 'Today';
  if (dOnly.getTime() === tomorrowOnly.getTime()) return 'Tomorrow';
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
};

/**
 * AvailableJobsView — list of unassigned jobs near the maid's home.
 *
 * Pulls from GET /api/jobs/available with a user-selectable radius (persisted
 * in localStorage so the chip selection survives reloads). Each card has its
 * own "Claim" button; on success we optimistically remove the job from the
 * list rather than re-fetching the whole page.
 */
const AvailableJobsView = ({ onClaimed }) => {
  const [radius, setRadius] = useState(() => {
    const stored = parseInt(localStorage.getItem(STORAGE_KEY), 10);
    return Number.isFinite(stored) && RADIUS_OPTIONS.includes(stored) ? stored : 25;
  });
  const [jobType, setJobType] = useState(() => localStorage.getItem(JOB_TYPE_STORAGE_KEY) || '');
  const [serviceType, setServiceType] = useState(() => localStorage.getItem(SERVICE_TYPE_STORAGE_KEY) || '');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');

  const [filtersOpen, setFiltersOpen] = useState(false);

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [maidHome, setMaidHome] = useState(null);

  const activeFilterCount = [radius !== 25, !!jobType, !!serviceType, minPrice !== '', maxPrice !== '']
    .filter(Boolean).length;

  // Jobs already arrive sorted by date then distance (GET /api/jobs/available);
  // group them in that same order so each date section stays sorted by distance.
  const groupedJobs = useMemo(() => {
    const groups = [];
    let lastLabel = null;
    for (const job of jobs) {
      const label = formatDateGroup(job.utcScheduledDate);
      if (label !== lastLabel) {
        groups.push({ label, jobs: [] });
        lastLabel = label;
      }
      groups[groups.length - 1].jobs.push(job);
    }
    return groups;
  }, [jobs]);

  const fetchAvailable = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { radiusMiles: radius };
      if (jobType) params.jobType = jobType;
      if (serviceType) params.serviceType = serviceType;
      if (minPrice !== '') params.minPrice = minPrice;
      if (maxPrice !== '') params.maxPrice = maxPrice;

      const res = await axios.get(`${API_URL}/api/jobs/available`, {
        headers: authHeaders(),
        params,
      });
      setJobs(Array.isArray(res.data?.jobs) ? res.data.jobs : []);
      setMaidHome(res.data?.maidHome || null);
    } catch (err) {
      logger.log('Failed to load available jobs:', err);
      setError(err?.response?.data?.error || 'Could not load available jobs.');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [radius, jobType, serviceType, minPrice, maxPrice]);

  useEffect(() => {
    // Debounced so typing in the price fields doesn't fire a request per keystroke.
    const timeoutId = setTimeout(fetchAvailable, 400);
    return () => clearTimeout(timeoutId);
  }, [fetchAvailable]);

  const handleRadiusChange = (value) => {
    setRadius(value);
    localStorage.setItem(STORAGE_KEY, String(value));
  };

  const handleJobTypeChange = (value) => {
    setJobType(value);
    localStorage.setItem(JOB_TYPE_STORAGE_KEY, value);
  };

  const handleServiceTypeChange = (value) => {
    setServiceType(value);
    localStorage.setItem(SERVICE_TYPE_STORAGE_KEY, value);
  };

  const handleClaim = async (jobId) => {
    // Throws on failure so the card surfaces the error inline
    await axios.post(`${API_URL}/api/jobs/${jobId}/claim`, {}, { headers: authHeaders() });
    setJobs((prev) => prev.filter((j) => j.id !== jobId));
    onClaimed?.(jobId);
  };

  return (
    <Box>
      {/* Header row */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Available jobs
          </Typography>
          {maidHome?.address && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <DistanceIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                from {maidHome.address}
              </Typography>
            </Box>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Badge badgeContent={activeFilterCount} color="primary" invisible={activeFilterCount === 0}>
            <Button
              size="small"
              variant={filtersOpen ? 'contained' : 'outlined'}
              startIcon={<FilterIcon />}
              onClick={() => setFiltersOpen((v) => !v)}
            >
              Filters
            </Button>
          </Badge>
          <Button size="small" startIcon={<RefreshIcon />} onClick={fetchAvailable} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Box>

      <Collapse in={filtersOpen}>
        <Box mb={1}>
          {/* Radius chips */}
          <Stack direction="row" spacing={1} mb={1.5} sx={{ overflowX: 'auto', pb: 0.5 }}>
            {RADIUS_OPTIONS.map((r) => (
              <Chip
                key={r}
                label={`${r} mi`}
                color={r === radius ? 'primary' : 'default'}
                variant={r === radius ? 'filled' : 'outlined'}
                onClick={() => handleRadiusChange(r)}
                clickable
                size="small"
              />
            ))}
          </Stack>

          {/* Job type chips */}
          <Stack direction="row" spacing={1} mb={1.5} sx={{ overflowX: 'auto', pb: 0.5 }}>
            {JOB_TYPE_OPTIONS.map((opt) => (
              <Chip
                key={opt.value}
                label={opt.label}
                color={opt.value === jobType ? 'primary' : 'default'}
                variant={opt.value === jobType ? 'filled' : 'outlined'}
                onClick={() => handleJobTypeChange(opt.value)}
                clickable
                size="small"
              />
            ))}
          </Stack>

          {/* Service type chips */}
          <Stack direction="row" spacing={1} mb={1.5} sx={{ overflowX: 'auto', pb: 0.5 }}>
            {SERVICE_TYPE_OPTIONS.map((opt) => (
              <Chip
                key={opt.value}
                label={opt.label}
                color={opt.value === serviceType ? 'primary' : 'default'}
                variant={opt.value === serviceType ? 'filled' : 'outlined'}
                onClick={() => handleServiceTypeChange(opt.value)}
                clickable
                size="small"
              />
            ))}
          </Stack>

          {/* Price range */}
          <Stack direction="row" spacing={1} mb={1.5} alignItems="center">
            <TextField
              size="small"
              type="number"
              placeholder="Min"
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              sx={{ width: 110 }}
            />
            <Typography variant="body2" color="text.secondary">to</Typography>
            <TextField
              size="small"
              type="number"
              placeholder="Max"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              sx={{ width: 110 }}
            />
          </Stack>
        </Box>
      </Collapse>

      {/* States */}
      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}

      {!loading && !error && jobs.length === 0 && (
        <Box textAlign="center" py={6}>
          <Typography color="text.secondary" gutterBottom>
            No available jobs within {radius} miles{(jobType || serviceType || minPrice || maxPrice) ? ' matching your filters' : ''}.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Try expanding the radius, adjusting your filters, or check back later.
          </Typography>
        </Box>
      )}

      {!loading && !error && groupedJobs.map((group) => (
        <Box key={group.label} mb={1}>
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              bgcolor: 'background.paper',
              py: 0.75,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2" fontWeight={700}>
                {group.label}
              </Typography>
              <Chip label={group.jobs.length} size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
            </Stack>
          </Box>
          {group.jobs.map((job) => (
            <AvailableJobCard key={job.id} job={job} onClaim={handleClaim} />
          ))}
        </Box>
      ))}
    </Box>
  );
};

export default AvailableJobsView;
